/**
 * App TSX 入口配置和一些全局的其他配置
 */

import { HeaderComp as CryptoHeaderComp } from '@/components/HeaderComp'
import { FooterViewHashSpace } from 'cloud-computing-power/components/FooterView'
import { HeaderCompHashSpace } from 'cloud-computing-power/components/HeaderComp'
import { FooterView } from 'crypto-otc/components/FooterView'
import { Platform } from 'platform-config'
import { useGlobalEventStore } from 'store'
import { merge } from 'utils'
import type { CustomDefineComponent } from './types'

const platform = import.meta.env.VITE_PLATFORM

/** 注册方式 */
type RegisterSource = {
  fromAccount?: boolean
  fromMobile?: boolean
  fromEmail?: boolean
}

type CompliantPosition = 'header' | 'footer'

type FooterArticleConfig = {
  title: string
  url?: string
  onClick?: () => void
}

type AppConfig = {
  /** 头部组件 */
  Header: CustomDefineComponent
  /** 底部组件 */
  Footer: CustomDefineComponent
  /** 是否是交易所 */
  isExchange: boolean
  /** 是否显示渲染视频 */
  isIntroduceVideo: boolean
  /** 是否做合规 */
  isCompliant: boolean
  /** 是否显示法律文件 */
  isShowLegalFile?: boolean
  /** 法律文件位置 */
  compliantPosition?: CompliantPosition
  /** 是否显示下载APP */
  isShowFooterAppDownload?: boolean
  /** 是否开启KYC认证 */
  isKYC?: boolean
  /** 官网地址 */
  officialWebsite?: string
  /** 隐藏的特色模块 */
  hideFeatureList?: FeatureName[]
  /** 是否开启主题切换 */
  isShowThemeSwitch?: boolean
  /** 是否打开云算力 */
  isOpenCloudComputingPower?: boolean
  /** 是否开启信用卡 */
  isOpenCreditCard?: boolean
  /** 注册方式 */
  registerSource?: RegisterSource
  /** 埋点信息 是否 上报 */
  isReport?: boolean
  /** 是否开启投资理财 */
  isOpenInvestFinancial?: boolean

  /** 补充关于我们链接 */
  aboutUs?: FooterArticleConfig[]
}

const isExchange = import.meta.env.VITE_CRYPTO_EXCHANGE === '1'

export type FeatureName = 'cloud' | 'debit' | 'enterprise' | 'bank' | 'assert' | 'partner' | 'mortgage'

const DEFAULT_REGISTER_SOURCE: RegisterSource = {
  fromAccount: true,
  fromMobile: true,
  fromEmail: true,
}

const PLATFORM_APP_CONFIG: Record<ImportMetaEnv['VITE_PLATFORM'], AppConfig> = {
  topcredit: {
    Header: CryptoHeaderComp,
    Footer: FooterView,
    isExchange,
    isIntroduceVideo: true,
    isCompliant: true,
    isShowFooterAppDownload: true,
    isKYC: true,
    isShowThemeSwitch: true,
    isOpenCloudComputingPower: true,
    isOpenCreditCard: true,
    isShowLegalFile: true,
    compliantPosition: 'header',
    isReport: true,
    isOpenInvestFinancial: true,
  },
  hashspace: {
    Header: HeaderCompHashSpace,
    Footer: FooterViewHashSpace,
    isExchange,
    isIntroduceVideo: false,
    isCompliant: false,
    officialWebsite: 'https://www.hashspace.one',
  },
  bge: {
    Header: HeaderCompHashSpace,
    Footer: FooterViewHashSpace,
    isExchange,
    isIntroduceVideo: false,
    isCompliant: true,
    isKYC: true,
    officialWebsite: 'https://www.bghash.com',
  },
  b9: {
    Header: CryptoHeaderComp,
    Footer: FooterView,
    isExchange,
    isIntroduceVideo: false,
    isCompliant: true,
    isShowFooterAppDownload: false,
    isKYC: false,
    isShowThemeSwitch: false,
    isOpenCloudComputingPower: false,
    hideFeatureList: ['cloud', 'debit'],
    compliantPosition: 'header',
    isOpenInvestFinancial: true,
  },
  '8v': {
    Header: CryptoHeaderComp,
    Footer: FooterView,
    isExchange,
    isIntroduceVideo: false,
    isCompliant: false,
    isShowFooterAppDownload: true,
    isKYC: true,
    isShowThemeSwitch: true,
    hideFeatureList: ['enterprise'],
    isOpenCreditCard: true,
    isOpenCloudComputingPower: true,
  },
  e97: {
    Header: CryptoHeaderComp,
    Footer: FooterView,
    isExchange,
    isIntroduceVideo: false,
    isCompliant: false,
    isShowFooterAppDownload: true,
    isKYC: true,
    isShowThemeSwitch: true,
    hideFeatureList: [],
    isOpenCreditCard: true,
    isOpenCloudComputingPower: true,
  },
  [Platform.E88]: {
    // 88站点
    Header: CryptoHeaderComp,
    Footer: FooterView,
    isExchange,
    isIntroduceVideo: false,
    isCompliant: true,
    isShowFooterAppDownload: false,
    isKYC: true,
    isShowThemeSwitch: false,
    isOpenCloudComputingPower: true,
    isOpenCreditCard: true,
    compliantPosition: 'header',
    registerSource: {
      fromAccount: false,
    },
    isOpenInvestFinancial: true,
    aboutUs: [
      {
        title: '商务合作',
        onClick: () => {
          const globalEventStore = useGlobalEventStore()
          globalEventStore.openCustomerService()
        },
      },
    ],
  },
}

/** #### App tsx 入口配置 */
export const APP_CONFIG: AppConfig = merge<unknown, Partial<AppConfig>, AppConfig>(
  {},
  {
    registerSource: DEFAULT_REGISTER_SOURCE,
  },
  PLATFORM_APP_CONFIG[platform],
)
